/* @flow */

import type { CombinedReducers } from '../../../../redux/reducers';
import type { ChannelMap } from '../types/Channel';
import type { NETGEM_API_V8_FEED } from '../types/FeedItem';
import type { NETGEM_API_V8_FEED_RESULT, NETGEM_API_V8_RAW_FEED } from '../types/FeedResult';
import type { NETGEM_API_V8_NTG_VIDEO_FEED, NETGEM_API_V8_URL_DEFINITION } from '../types/NtgVideoFeed';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../types/RequestResponse';
import type { NETGEM_API_V8_SECTION, SectionModelUnionType } from '../types/Section';

import { type AllSettledPromises, SettledPromiseFulfilled } from '../../../../helpers/jsHelpers/promise';
import { generateApiUrl } from '../../../../redux/netgemApi/actions/helpers/api';
import { HeaderName, HeaderValue } from '../constants/Headers';
import { filterFeed, flattenFeedIfRequested, mergeFeeds } from '../helpers/Feed';
import { buildHeaderList } from '../helpers/RequestHeaders';
import { FeedProviderKind } from '../types/Feed';
import { HttpMethod } from '../types/HttpMethod';
import { NETGEM_API_V8_AUTHENT_REALM_NETGEM, NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR } from '../types/Realm';
import sendV8Request from './v8';

type SourceConfigType = {
  ...NETGEM_API_V8_NTG_VIDEO_FEED,
  feedIndex: number,
};

export type FeedRequestResponseType = {
  feedIndex: number,
  result: NETGEM_API_V8_FEED_RESULT,
  status?: number,
};

type NetworkFeedsArrayType = Array<NETGEM_API_V8_RAW_FEED>;

const sendFeedRequestInternal: (
  url: string,
  method: HttpMethod,
  authenticationToken: string | null,
  extraHeaders: Array<string> | null,
  feedIndex: number,
  state: CombinedReducers,
  signal: ?AbortSignal,
) => Promise<FeedRequestResponseType> = (url, method, authenticationToken, extraHeaders, feedIndex, state, signal) => {
  const extraHeaderList = extraHeaders ?? [null];
  const headerList = buildHeaderList(HeaderName.Accept, HeaderValue.ApplicationJson, ...extraHeaderList);

  return sendV8Request(url, authenticationToken, null, headerList, state, signal).then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
    const { result } = data;

    return Promise.resolve({
      feedIndex,
      result,
    });
  });
};

const filterMergeSourcesFeed: (section: NETGEM_API_V8_SECTION, networkFeedsArray: NetworkFeedsArrayType, channels: ChannelMap, sessionId: string) => NETGEM_API_V8_FEED = (
  section,
  networkFeedsArray,
  channels,
  sessionId,
) => {
  const { length: feedCount } = networkFeedsArray;

  if (feedCount === 0) {
    return [];
  }

  // Filter feed sources
  const filteredFeedsArray: Array<NETGEM_API_V8_FEED> = [];

  const sectionModel: SectionModelUnionType = section.model;
  const { scoring } = sectionModel;
  const { filter: sectionFilter, provider: sectionProvider, slice: sectionSlice } = sectionModel;
  const groupItemsBy = section.widgetConfig?.groupItemsBy;

  if (sectionProvider === FeedProviderKind.Aggregation) {
    const { sources: aggregationSources } = sectionModel;

    if (!aggregationSources) {
      return [];
    }

    for (let i = 0; i < feedCount; i++) {
      const {
        [i]: { filter, slice },
      } = aggregationSources;

      let filteredFeed = filterFeed(networkFeedsArray[i], channels, filter, slice);
      if (filteredFeed.length > 0) {
        if (sectionFilter) {
          filteredFeed = filterFeed([...filteredFeed] as Array<Object> as NETGEM_API_V8_RAW_FEED, channels, sectionFilter, slice);
        }
        filteredFeedsArray.push(flattenFeedIfRequested(filteredFeed, groupItemsBy));
      }
    }
  } else if (sectionProvider === FeedProviderKind.NtgVideo) {
    filteredFeedsArray.push(flattenFeedIfRequested(filterFeed(networkFeedsArray[0], channels, sectionFilter), groupItemsBy));
  }

  // Merge and sort feed sources
  return mergeFeeds(filteredFeedsArray, scoring, { sessionId }, sectionSlice);
};

const sendFeedRequest: (section: NETGEM_API_V8_SECTION, searchString: ?string, state: CombinedReducers, sourceList: Array<SourceConfigType>, signal?: AbortSignal) => Promise<NETGEM_API_V8_FEED> = (
  section,
  searchString,
  state,
  sourceList,
  signal,
) => {
  const { length: sourceCount } = sourceList;
  const {
    appConfiguration: { deviceChannels, distributorAppKeys, mainDistributorId, sessionId },
    appRegistration,
  } = state;

  if (sourceCount === 0) {
    return Promise.resolve([]);
  }

  const promises: Array<Promise<FeedRequestResponseType>> = [];
  const additionalParameters = searchString ? { searchString } : {};

  for (let i: number = 0; i < sourceCount; i++) {
    const source = sourceList[i];
    const { authent, authentRealm, feedIndex, method, params, provider, sakName, uri } = source;

    const urlDefinition: NETGEM_API_V8_URL_DEFINITION = {
      authent,
      authentRealm,
      method,
      params,
      provider: provider as string,
      sakName,
      uri,
    };

    const url = generateApiUrl(urlDefinition, additionalParameters, state);

    let authenticationToken: string | null = null;
    let extraHeaders: Array<string> | null = null;

    if (authentRealm === NETGEM_API_V8_AUTHENT_REALM_NETGEM) {
      // Use PTF authentication token
      ({ authenticationToken } = appRegistration);
    } else if (authentRealm) {
      /*
       * Examples of authentRealm:
       *   authentRealm: "videofutur"
       *   authentRealm: "videofutur?distributor=vitis-vno-fibre-v8"
       *   authentRealm: "videofutur?distributor=vitis-vno-fibre-v8&foo=bar"
       *
       * NOTE: Additional parameters are not currently used
       */
      const m = authentRealm.match(/^(.+?)(?:\?distributor=(.+?)(&.+?=.+)?)?$/iu);

      if (m) {
        const [, realm, distributorId] = m;

        if (realm === NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR) {
          // Use BO  authentication token
          const distributor = distributorId ?? mainDistributorId;

          if (distributor) {
            const { [distributor]: appKey } = distributorAppKeys;
            extraHeaders = [HeaderName.AppKey, appKey];
            ({ authenticationToken } = appRegistration);
          }
        }
      }
    }

    promises.push(sendFeedRequestInternal(url, HttpMethod.GET, authenticationToken, extraHeaders, feedIndex, state, signal));
  }

  return Promise.allSettled(promises).then((results: AllSettledPromises<FeedRequestResponseType>) => {
    const networkFeedsArray: NetworkFeedsArrayType = [];
    for (let i = 0; i < sourceCount; ++i) {
      networkFeedsArray.push([]);
    }

    results.forEach(({ status, value }) => {
      if (status === SettledPromiseFulfilled) {
        const {
          feedIndex,
          result: { feed },
        } = value as Object as FeedRequestResponseType;
        networkFeedsArray[feedIndex] = feed;
      }
    });

    return filterMergeSourcesFeed(section, networkFeedsArray, deviceChannels, sessionId);
  });
};

const sendV8SectionFeedRequest: (section: NETGEM_API_V8_SECTION, searchString: ?string, state: CombinedReducers, signal?: AbortSignal) => Promise<NETGEM_API_V8_FEED> = (
  section,
  searchString,
  state,
  signal,
) => {
  const sourceList: Array<SourceConfigType> = [];

  const sectionModel: SectionModelUnionType = section.model;

  if (sectionModel.provider === FeedProviderKind.Aggregation) {
    const { sources } = sectionModel;

    if (!sources) {
      return Promise.reject(new Error('Missing sources in feed'));
    }

    for (let i: number = 0; i < sources.length; i++) {
      sourceList.push({
        ...sources[i],
        feedIndex: i,
      });
    }
  } else if (sectionModel.provider === FeedProviderKind.NtgVideo) {
    const { authent, authentRealm, filter, method, params = [], provider, queryType, sakName, scoring, slice, uri = '' } = sectionModel;
    const ntgVideoFeed: NETGEM_API_V8_NTG_VIDEO_FEED = {
      authent,
      authentRealm,
      filter,
      method,
      params,
      provider,
      queryType,
      sakName,
      scoring,
      slice,
      uri,
    };
    sourceList.push({
      ...ntgVideoFeed,
      feedIndex: 0,
    });
  }

  return sendFeedRequest(section, searchString, state, sourceList, signal);
};

export { sendV8SectionFeedRequest };
